"use client";

import { TypeFormWithoutUnresolvableLinksResponse } from "@2u-landing-pages/types";
import { renderText } from "@2u-landing-pages/utils/contentful-richtext";
import EventClient from "@2u-landing-pages/utils/event-client";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { Body } from "@red-digital/bricks";
import cn from "classnames";
import Script from "next/script";
import type { HTMLProps } from "react";
import React, { useId, useState } from "react";
import styles from "./LeadForm.module.scss";
import { useFormEvents } from "./useFormEvents";

interface LeadFormProps extends HTMLProps<HTMLDivElement> {
  packageId: string;
  enableStyles?: boolean;
  loadedClassName?: string;
  footnote?: TypeFormWithoutUnresolvableLinksResponse["fields"]["footnote"];
  phoneNumber?: string;
  parseMetadata?: (text: string) => string;
  isLoading?: boolean;
}

export const LeadForm: React.FC<LeadFormProps> = ({
  className,
  loadedClassName = "",
  packageId = "",
  enableStyles = true,
  footnote,
  phoneNumber,
  parseMetadata,
  isLoading,
  ...rest
}) => {
  const [loaded, setLoaded] = useState(false);
  const salt = useId();
  const formId = `twou-form-${packageId}-${salt}`;
  const { formContainerRef, steps } = useFormEvents(formId);
  const isLastStep = steps.current && steps.total && steps.current === steps.total;

  const handleExternalLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    EventClient.elementClicked({
      webElement: {
        elementType: "Landing Page",
        htmlId: event.currentTarget.href,
        location: "LoadForm",
        name: event.currentTarget.innerText,
        position: "",
        text: "",
      },
    });
  };

  return (
    <div className={className} ref={formContainerRef}>
      {!isLoading && (
        <>
          <div
            {...rest}
            id={formId}
            className={cn("self-mount-plugin", {
              [`${loadedClassName}`]: loaded,
              [styles.leadForm]: enableStyles,
              ["taxi-last-step"]: isLastStep,
            })}
            data-template-type="embed-inline"
            data-package-type="fetch"
            data-package-id={packageId}
          />
          {footnote &&
            documentToReactComponents(footnote, {
              renderText: (text) => renderText(parseMetadata?.(text) || text, phoneNumber),
              renderNode: {
                [BLOCKS.PARAGRAPH]: (_node: any, children: React.ReactNode) => (
                  <Body
                    className={cn(styles.footnote, {
                      [styles.footnoteHidden]: isLastStep,
                    })}
                    variant="caption-md"
                  >
                    {children}
                  </Body>
                ),
                [INLINES.HYPERLINK]: (node: any) => {
                  const { uri } = node?.data;
                  const content = node?.content?.[0];
                  if (content && "value" in content) {
                    return (
                      <a href={uri} target="_blank" rel="noopener noreferrer" onClick={handleExternalLinkClick}>
                        {content.value}
                      </a>
                    );
                  }
                  return null;
                },
              },
            })}
          <Script
            src="https://prospect-form-plugin.2u.com/cask-latest/lead.form.plugin.js"
            onLoad={() => {
              setLoaded(true);
            }}
          />
        </>
      )}
    </div>
  );
};
